import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, User, Phone, Mail, MapPin, CheckCircle, Loader2, 
  ArrowLeft, ArrowRight, Award, Users, Star, Zap, Shield,
  Briefcase, IndianRupee, Clock, MessageCircle, PhoneCall,
  FileCheck, BadgeCheck, CreditCard, Building, ChevronRight,
  Sparkles, TrendingUp, GraduationCap, Check, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_TYPES = {
  TC: { 
    name: "Training Center", 
    fee: 50000, 
    description: "Multiple centers allowed in a district",
    features: ["Local training operations", "Student enrollment", "Exam center"],
    color: "emerald",
    popular: true
  },
  DLC: { 
    name: "District Partner", 
    fee: 150000, 
    description: "Exclusive district rights",
    features: ["District level control", "Sub-center management", "Priority support"],
    color: "blue",
    popular: false
  },
  SP: { 
    name: "State Partner", 
    fee: 500000, 
    description: "Complete state operations control",
    features: ["State level authority", "Revenue sharing", "Master franchise rights"],
    color: "amber",
    popular: false
  }
};

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

const investmentBudgets = [
  "₹50,000 - ₹1,00,000",
  "₹1,00,000 - ₹2,00,000",
  "₹2,00,000 - ₹5,00,000",
  "₹5,00,000+"
];

const FranchiseApplyPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0); // 0 = category, 1-3 = form steps
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [districts, setDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [availability, setAvailability] = useState({ available: true, message: '' });
  
  const [formData, setFormData] = useState({
    partner_type: '',
    // Step 1: Basic Details
    owner_name: '',
    mobile: '',
    email: '',
    city: '',
    // Step 2: Business Details
    center_name: '',
    proposed_location: '',
    investment_budget: '',
    relevant_experience: '', // Yes/No
    experience_type: '', // Training Center, School, College, ITI, Other
    experience_other: '', // If Other selected
    // Step 3: Additional Details
    address: '',
    district: '',
    state: '',
    pin_code: '',
    why_join: ''
  });

  // Fetch districts when state changes
  useEffect(() => {
    if (formData.state) {
      fetchDistricts(formData.state);
    }
  }, [formData.state]);

  // Check availability for SP and DLC
  useEffect(() => {
    if ((formData.partner_type === 'SP' && formData.state) || 
        (formData.partner_type === 'DLC' && formData.state && formData.district)) {
      checkAvailability();
    }
  }, [formData.partner_type, formData.state, formData.district]);

  const fetchDistricts = async (state) => {
    setLoadingDistricts(true);
    try {
      const response = await axios.get(`${API}/districts/${encodeURIComponent(state)}`);
      setDistricts(response.data.districts || []);
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
    }
    setLoadingDistricts(false);
  };

  const checkAvailability = async () => {
    try {
      const response = await axios.post(`${API}/partner/availability`, {
        partner_type: formData.partner_type,
        state: formData.state,
        district: formData.district
      });
      setAvailability({
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      console.error('Error checking availability:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 10) });
    } else if (name === 'pin_code') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 6) });
    } else if (name === 'state') {
      setFormData({ ...formData, [name]: value, district: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const selectPartnerType = (type) => {
    setFormData({ ...formData, partner_type: type });
    setCurrentStep(1);
  };

  const validateStep = (step) => {
    switch(step) {
      case 1:
        if (!formData.owner_name || !formData.mobile || !formData.email || !formData.city) {
          toast.error('Please fill all required fields');
          return false;
        }
        if (formData.mobile.length !== 10) {
          toast.error('Please enter valid 10-digit mobile number');
          return false;
        }
        if (!formData.email.includes('@')) {
          toast.error('Please enter valid email address');
          return false;
        }
        return true;
      case 2:
        if (!formData.center_name || !formData.proposed_location || !formData.investment_budget) {
          toast.error('Please fill all required fields');
          return false;
        }
        return true;
      case 3:
        if (!formData.address || !formData.state || !formData.district) {
          toast.error('Please fill all required fields');
          return false;
        }
        if (!availability.available && formData.partner_type !== 'TC') {
          toast.error('This location is not available for the selected partner type');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        fee: PARTNER_TYPES[formData.partner_type].fee,
        partner_type_name: PARTNER_TYPES[formData.partner_type].name
      };
      
      const response = await axios.post(`${API}/partner/apply`, submitData);
      
      setApplicationId(response.data.application_id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    }
    setIsSubmitting(false);
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Application Submitted! 🎉
          </h1>
          
          <p className="text-gray-600 mb-6">
            Your partner application has been received. Login credentials have been sent to your email.
          </p>

          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-500 mb-1">Application ID</p>
            <p className="text-xl font-bold text-blue-600 font-mono">{applicationId}</p>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold text-emerald-800 mb-2">📧 Check Your Email</h3>
            <p className="text-sm text-emerald-700">
              We've sent login credentials to <strong>{formData.email}</strong>. Use these to track your application status.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-left">
            <h3 className="font-semibold text-blue-800 mb-2">📋 What's Next?</h3>
            <ul className="text-sm text-blue-700 space-y-2">
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-green-600" />
                <span>Login to track your application status</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-green-600" />
                <span>Our team will review your application</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-green-600" />
                <span>After Stage 1 approval, complete the payment</span>
              </li>
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 mt-0.5 text-green-600" />
                <span>Your center dashboard will be activated!</span>
              </li>
            </ul>
          </div>

          <div className="flex gap-4">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="flex-1"
            >
              Go Home
            </Button>
            <Button 
              onClick={() => navigate('/auth/login')}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              Login to Track
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button 
            onClick={() => navigate('/partner')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Partner Page
          </button>
          
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4 text-yellow-300" />
                Limited seats available in your area
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-3">
                Start Your Own Training Center<br />
                <span className="text-yellow-300">& Earn Monthly Income</span>
              </h1>
              <p className="text-blue-100 text-lg">
                Join 100+ partners earning ₹50,000+ per month with Iqra Skills
              </p>
              
              <div className="flex flex-wrap gap-3 mt-4">
                <span className="inline-flex items-center gap-1 bg-green-500/80 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="w-4 h-4" /> Govt Projects
                </span>
                <span className="inline-flex items-center gap-1 bg-amber-500/80 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="w-4 h-4" /> Low Investment
                </span>
                <span className="inline-flex items-center gap-1 bg-purple-500/80 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="w-4 h-4" /> Full Support
                </span>
              </div>
            </div>
            
            {/* Trust Stats */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center">
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-bold text-yellow-300">100+</p>
                  <p className="text-sm text-blue-100">Active Centers</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-yellow-300">25K+</p>
                  <p className="text-sm text-blue-100">Students</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-yellow-300">15+</p>
                  <p className="text-sm text-blue-100">Years</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form Area */}
          <div className="lg:col-span-2">
            {/* Progress Bar */}
            {currentStep > 0 && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/80 text-sm">Step {currentStep} of 3</span>
                  <span className="text-white/80 text-sm">{Math.round((currentStep/3)*100)}% Complete</span>
                </div>
                <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-500"
                    style={{ width: `${(currentStep/3)*100}%` }}
                  />
                </div>
                <div className="flex justify-between mt-3">
                  {['Basic Details', 'Business Details', 'Address & Submit'].map((label, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        currentStep > idx + 1 ? 'bg-green-500 text-white' :
                        currentStep === idx + 1 ? 'bg-blue-500 text-white' :
                        'bg-white/20 text-white/50'
                      }`}>
                        {currentStep > idx + 1 ? <Check className="w-4 h-4" /> : idx + 1}
                      </div>
                      <span className={`text-xs hidden sm:block ${currentStep === idx + 1 ? 'text-white' : 'text-white/50'}`}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 0: Partner Category Selection */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Choose Your Partner Category</h2>
                  <p className="text-white/60">Select the type of partnership that suits you best</p>
                </div>

                <div className="grid gap-4">
                  {Object.entries(PARTNER_TYPES).map(([key, type]) => (
                    <button
                      key={key}
                      onClick={() => selectPartnerType(key)}
                      className={`relative bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 ${
                        formData.partner_type === key ? 'border-yellow-400' : 'border-transparent'
                      } rounded-2xl p-6 text-left transition-all group`}
                    >
                      {type.popular && (
                        <div className="absolute -top-3 right-4 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <Zap className="w-3 h-3" /> MOST POPULAR
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-1">{type.name}</h3>
                          <p className="text-white/60 text-sm mb-3">{type.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            {type.features.map((feature, idx) => (
                              <span key={idx} className="inline-flex items-center gap-1 bg-white/10 text-white/80 px-2 py-1 rounded-lg text-xs">
                                <CheckCircle className="w-3 h-3 text-green-400" />
                                {feature}
                              </span>
                            ))}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-3xl font-bold text-yellow-400">
                            ₹{type.fee.toLocaleString()}
                          </p>
                          <p className="text-white/50 text-sm">One-time fee</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                        <span className="text-white/60 text-sm">Click to select</span>
                        <ChevronRight className="w-5 h-5 text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Basic Details */}
            {currentStep === 1 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Basic Details</h2>
                    <p className="text-gray-500 text-sm">Tell us about yourself</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label htmlFor="owner_name" className="text-gray-700 font-medium">
                      Full Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      value={formData.owner_name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="mobile" className="text-gray-700 font-medium">
                      Mobile Number <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+91</span>
                      <Input
                        id="mobile"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        placeholder="9521484003"
                        className="pl-12"
                        maxLength={10}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-gray-700 font-medium">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Enter your city"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-4 mt-8">
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    className="flex-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={nextStep}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Business Details */}
            {currentStep === 2 && (
              <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Business Details</h2>
                    <p className="text-gray-500 text-sm">About your training center</p>
                  </div>
                </div>

                <div className="space-y-5">
                  <div>
                    <Label htmlFor="center_name" className="text-gray-700 font-medium">
                      Center Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="center_name"
                      name="center_name"
                      value={formData.center_name}
                      onChange={handleChange}
                      placeholder="e.g., Iqra Skills Training Center"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="proposed_location" className="text-gray-700 font-medium">
                      Proposed Location <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="proposed_location"
                      name="proposed_location"
                      value={formData.proposed_location}
                      onChange={handleChange}
                      placeholder="Area/Locality where you plan to open"
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="investment_budget" className="text-gray-700 font-medium">
                      Investment Budget <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="investment_budget"
                      name="investment_budget"
                      value={formData.investment_budget}
                      onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select budget range</option>
                      {investmentBudgets.map((budget, idx) => (
                        <option key={idx} value={budget}>{budget}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="relevant_experience" className="text-gray-700 font-medium">
                      Relevant Experience <span className="text-red-500">*</span>
                    </Label>
                    <select
                      id="relevant_experience"
                      name="relevant_experience"
                      value={formData.relevant_experience}
                      onChange={handleChange}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>

                  {/* Conditional: If Yes, show experience type */}
                  {formData.relevant_experience === 'Yes' && (
                    <div className="space-y-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div>
                        <Label htmlFor="experience_type" className="text-gray-700 font-medium">
                          What type of experience do you have? <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="experience_type"
                          name="experience_type"
                          value={formData.experience_type}
                          onChange={handleChange}
                          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          required
                        >
                          <option value="">Select Experience Type</option>
                          <option value="Training Center">Training Center</option>
                          <option value="School">School</option>
                          <option value="College">College</option>
                          <option value="ITI">ITI</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      {/* If Other selected, show text input */}
                      {formData.experience_type === 'Other' && (
                        <div>
                          <Label htmlFor="experience_other" className="text-gray-700 font-medium">
                            Please specify <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="experience_other"
                            name="experience_other"
                            value={formData.experience_other}
                            onChange={handleChange}
                            placeholder="Describe your experience"
                            className="mt-1"
                            required
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex gap-4 mt-8">
                  <Button 
                    variant="outline" 
                    onClick={prevStep}
                    className="flex-1"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={nextStep}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Additional Details & Submit */}
            {currentStep === 3 && (
              <form onSubmit={handleSubmit}>
                <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Address Details</h2>
                      <p className="text-gray-500 text-sm">Where will your center be located?</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="state" className="text-gray-700 font-medium">
                        State <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Select State</option>
                        {indianStates.map((state, idx) => (
                          <option key={idx} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="district" className="text-gray-700 font-medium">
                        District <span className="text-red-500">*</span>
                      </Label>
                      <select
                        id="district"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                        disabled={!formData.state || loadingDistricts}
                      >
                        <option value="">
                          {loadingDistricts ? 'Loading...' : 'Select District'}
                        </option>
                        {districts.map((district, idx) => (
                          <option key={idx} value={district}>{district}</option>
                        ))}
                      </select>
                      
                      {/* Availability Status */}
                      {formData.partner_type !== 'TC' && formData.state && formData.district && (
                        <div className={`mt-2 p-3 rounded-lg ${
                          availability.available 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-red-50 border border-red-200'
                        }`}>
                          <p className={`text-sm flex items-center gap-2 ${
                            availability.available ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {availability.available ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            {availability.message || (availability.available ? 'Available!' : 'Not available')}
                          </p>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="address" className="text-gray-700 font-medium">
                        Full Address <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter complete address"
                        className="mt-1"
                        rows={3}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="pin_code" className="text-gray-700 font-medium">
                        PIN Code
                      </Label>
                      <Input
                        id="pin_code"
                        name="pin_code"
                        value={formData.pin_code}
                        onChange={handleChange}
                        placeholder="6-digit PIN"
                        className="mt-1"
                        maxLength={6}
                      />
                    </div>

                    <div>
                      <Label htmlFor="why_join" className="text-gray-700 font-medium">
                        Why do you want to join Iqra Skills? <span className="text-gray-400">(Optional)</span>
                      </Label>
                      <Textarea
                        id="why_join"
                        name="why_join"
                        value={formData.why_join}
                        onChange={handleChange}
                        placeholder="Tell us your motivation..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="bg-gray-50 rounded-xl p-4 mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Application Summary</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">Partner Type:</span>
                        <p className="font-medium">{PARTNER_TYPES[formData.partner_type]?.name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Fee:</span>
                        <p className="font-medium text-green-600">
                          ₹{PARTNER_TYPES[formData.partner_type]?.fee.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Name:</span>
                        <p className="font-medium">{formData.owner_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Center:</span>
                        <p className="font-medium">{formData.center_name}</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={prevStep}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      disabled={isSubmitting || (!availability.available && formData.partner_type !== 'TC')}
                      className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-2" />
                          Submit Application
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Trust Panel - Side Section */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-6">
              {/* Trust Stats */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-yellow-400" />
                  Why Trust Us?
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">100+ Active Centers</p>
                      <p className="text-white/60 text-sm">Across India</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">25,000+ Students</p>
                      <p className="text-white/60 text-sm">Trained & Placed</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                      <Award className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Govt Affiliated</p>
                      <p className="text-white/60 text-sm">NSDC & Skill India</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ROI Quote */}
              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 text-white">
                <div className="flex items-start gap-3">
                  <div className="text-4xl">"</div>
                  <div>
                    <p className="font-medium leading-relaxed">
                      Most partners recover their investment in just 2-3 months and start earning profit!
                    </p>
                    <p className="text-green-100 text-sm mt-2">— Iqra Skills Partner Network</p>
                  </div>
                </div>
              </div>

              {/* Contact Options */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <h3 className="text-white font-bold mb-4">Need Help?</h3>
                
                <div className="space-y-3">
                  <Button 
                    onClick={() => window.open('tel:+919521484003')}
                    className="w-full bg-green-500 hover:bg-green-600 text-white"
                  >
                    <PhoneCall className="w-4 h-4 mr-2" />
                    Call Now
                  </Button>
                  
                  <Button 
                    onClick={() => window.open('https://wa.me/919521484003')}
                    variant="outline"
                    className="w-full border-white/30 text-white hover:bg-white/10"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </div>
              </div>

              {/* Selected Partner Type Info */}
              {formData.partner_type && (
                <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-2xl p-6">
                  <h3 className="text-yellow-400 font-bold mb-2">Selected Plan</h3>
                  <p className="text-white text-lg font-semibold">
                    {PARTNER_TYPES[formData.partner_type].name}
                  </p>
                  <p className="text-2xl font-bold text-yellow-400 mt-1">
                    ₹{PARTNER_TYPES[formData.partner_type].fee.toLocaleString()}
                  </p>
                  <p className="text-white/60 text-sm mt-2">
                    {PARTNER_TYPES[formData.partner_type].description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FranchiseApplyPage;
